import { useEffect, useMemo } from "react";
import CalendarComponent from "./CalendarComponent";
import TimeSlotSelector from "../booking/TimeSlotSelector";
import SlotPreferenceBlock from "./SlotPreferenceBlock";
import {
  TimeSlot,
  BookingLanguage,
  BookingStep,
  FastBookingSlots,
} from "@/types/booking";
import type { ExternalSlot } from "@/hooks/useClinicExternalSlots";
import { useSlotSelectionStateMachine } from "@/hooks/useSlotSelectionStateMachine";
import type { SlotSelectionUpdateOptions } from "@/hooks/booking/useTimeSlots";
import { useDateStepLogic } from "./useDateStepLogic";

interface DesktopDateStepProps {
  currentDate: Date | undefined;
  onDateSelect: (date: Date | undefined) => void;
  fromDate: Date;
  maxDate: Date;
  language: BookingLanguage;
  selectedTimeSlots: TimeSlot[];
  getIsDateDisabled: (excludeSlotIndex?: number) => (date: Date) => boolean;
  hasTimeSlotForDate: (date: Date) => boolean;
  bookingType: "request";
  selectedClinic: string;
  onTimeSelect?: (
    times: string[],
    options?: SlotSelectionUpdateOptions,
  ) => void;
  onDeleteTimeSlot?: (slot: TimeSlot) => void;
  setCurrentStep?: (step: BookingStep) => void;
  setBookingType?: (type: "request") => void;
  onBack?: () => void;
  fastBookingSlots?: FastBookingSlots;
  isLoading?: boolean;
  showSlotIndicators?: boolean;
  useExternalSlotsOnly?: boolean;
  externalSlots?: ExternalSlot[] | null;
  // Controlled expanded state from parent
  controlledFirstSlotExpanded?: boolean;
  onFirstSlotExpandedChange?: (expanded: boolean) => void;
  controlledSecondSlotExpanded?: boolean;
  onSecondSlotExpandedChange?: (expanded: boolean) => void;
}

const DesktopDateStep = ({
  currentDate,
  onDateSelect,
  fromDate,
  maxDate,
  language,
  selectedTimeSlots,
  getIsDateDisabled,
  hasTimeSlotForDate,
  bookingType,
  selectedClinic,
  onTimeSelect,
  onDeleteTimeSlot,
  setCurrentStep,
  setBookingType,
  onBack,
  fastBookingSlots,
  isLoading = false,
  showSlotIndicators = false,
  useExternalSlotsOnly = false,
  externalSlots,
  controlledFirstSlotExpanded,
  onFirstSlotExpandedChange,
  controlledSecondSlotExpanded,
  onSecondSlotExpandedChange,
}: DesktopDateStepProps) => {
  // Get state and actions from store via shared logic hook
  const {
    selectedDate: storeSelectedDate,
    selectedTimeSlots: storeSelectedTimeSlots,
    selectDate: storeSelectDate,
    selectTimeSlot: storeSelectTimeSlot,
    deleteTimeSlot: storeDeleteTimeSlot,
    setSecondSlotCalendarDate: storeSetSecondSlotCalendarDate,
    secondSlotCalendarDate: storeSecondSlotCalendarDate,
    defaultSecondSlotDate,
  } = useDateStepLogic();

  const firstSlot = storeSelectedTimeSlots[0];
  const secondSlot = storeSelectedTimeSlots[1];

  const internalStateMachine = useSlotSelectionStateMachine({
    firstSlot,
    secondSlot,
  });

  // Use controlled state if provided, otherwise use internal state machine
  const isFirstSlotExpanded =
    controlledFirstSlotExpanded ?? internalStateMachine.isFirstSlotExpanded;
  const isSecondSlotExpanded =
    controlledSecondSlotExpanded ?? internalStateMachine.isSecondSlotExpanded;

  // Wrap state machine actions to also notify parent when controlled
  const stateMachine = {
    ...internalStateMachine,
    isFirstSlotExpanded,
    isSecondSlotExpanded,
    onEditFirstSlot: () => {
      internalStateMachine.onEditFirstSlot();
      onFirstSlotExpandedChange?.(true);
    },
    onDoneEditingFirstSlot: () => {
      internalStateMachine.onDoneEditingFirstSlot();
      onFirstSlotExpandedChange?.(false);
    },
    onEditSecondSlot: () => {
      internalStateMachine.onEditSecondSlot();
      onSecondSlotExpandedChange?.(true);
    },
    onAddSecondSlot: () => {
      internalStateMachine.onAddSecondSlot();
      onSecondSlotExpandedChange?.(true);
    },
    onDoneEditingSecondSlot: () => {
      internalStateMachine.onDoneEditingSecondSlot();
      onSecondSlotExpandedChange?.(false);
    },
    onDeleteSecondSlot: () => {
      internalStateMachine.onDeleteSecondSlot();
      onSecondSlotExpandedChange?.(false);
    },
  };

  // Pre-compute stable isDateDisabled variants to avoid defeating CalendarComponent memo
  const isDateDisabledForFirstSlot = useMemo(
    () => getIsDateDisabled(stateMachine.isFirstSlotExpanded ? 0 : undefined),
    [getIsDateDisabled, stateMachine.isFirstSlotExpanded],
  );
  const isDateDisabledForSecondSlot = useMemo(
    () => getIsDateDisabled(stateMachine.isSecondSlotExpanded ? 1 : undefined),
    [getIsDateDisabled, stateMachine.isSecondSlotExpanded],
  );

  // Use store's selectedDate as the current date for first slot
  // This automatically syncs when the store updates
  const firstSlotCurrentDate = storeSelectedDate || currentDate;

  // When editing first slot, use its selected date or the slot's date or the current date (if auto-selected)
  const firstSlotDate = stateMachine.isFirstSlotExpanded
    ? firstSlotCurrentDate
    : (firstSlot?.date ?? firstSlotCurrentDate);

  // For second slot, use the slot's date if it exists, otherwise use the default from store
  // The store calculates the next available date automatically
  const secondSlotDate = stateMachine.isSecondSlotExpanded
    ? storeSecondSlotCalendarDate || secondSlot?.date || defaultSecondSlotDate
    : secondSlot?.date;

  const shouldRenderSecondSlot = stateMachine.showSecondSlot;

  const canEditSecondSlot = !stateMachine.isFirstSlotExpanded;

  // Handle date selection for first slot - delegate to store
  const handleFirstDateSelect = (date: Date | undefined) => {
    if (stateMachine.isFirstSlotExpanded && date) {
      console.log("[First Slot] Date selected:", date);
      storeSelectDate(date);
    }
  };

  // Handle date selection for second slot - track in store without overriding first slot
  const handleSecondDateSelect = (date: Date | undefined) => {
    if (stateMachine.isSecondSlotExpanded && date) {
      console.log("[Second Slot] Date selected:", date);
      // Store in global state for calendar display, don't override store's first slot date
      storeSetSecondSlotCalendarDate(date);
    }
  };

  // Handle time selection for first slot - delegate to store
  const handleFirstTimeSelect = (
    times: string[],
    options?: SlotSelectionUpdateOptions,
  ) => {
    if (!firstSlotDate) return;

    if (stateMachine.isFirstSlotExpanded && times.length > 0) {
      storeSelectTimeSlot(times, {
        previousSlot: firstSlot,
        preference: "first",
        dateOverride: firstSlotDate,
        isFromExternalSlot: options?.isFromExternalSlot,
      });
    }
  };

  // Handle time selection for second slot - use calendar date if selected
  const handleSecondTimeSelect = (
    times: string[],
    options?: SlotSelectionUpdateOptions,
  ) => {
    if (!secondSlotDate) return;

    if (stateMachine.isSecondSlotExpanded && times.length > 0) {
      storeSelectTimeSlot(times, {
        previousSlot: secondSlot,
        preference: "second",
        dateOverride: storeSecondSlotCalendarDate || secondSlotDate,
        isFromExternalSlot: options?.isFromExternalSlot,
      });
      // Clear the calendar date after storing
      storeSetSecondSlotCalendarDate(null);
    }
  };

  // Auto-collapse first slot when a slot is selected, then open second slot
  useEffect(() => {
    if (firstSlot && stateMachine.isFirstSlotExpanded) {
      // First, close the first slot
      stateMachine.onDoneEditingFirstSlot();

      // Then, after the first slot closes, open the second slot if it doesn't exist
      if (!secondSlot) {
        const id = setTimeout(() => {
          // Pre-set the second slot calendar date so it opens on the right month
          if (defaultSecondSlotDate) {
            storeSetSecondSlotCalendarDate(defaultSecondSlotDate);
          }
          stateMachine.onAddSecondSlot();
        }, 600); // Wait for first slot close animation (490ms duration + 105ms delay + buffer)
        return () => clearTimeout(id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstSlot]);

  // Auto-collapse second slot when a slot is selected
  useEffect(() => {
    if (secondSlot && stateMachine.isSecondSlotExpanded) {
      stateMachine.onDoneEditingSecondSlot();
    }
  }, [secondSlot]);

  const handleDeleteSecondSlot = () => {
    if (secondSlot) {
      storeDeleteTimeSlot(secondSlot);
      stateMachine.onDeleteSecondSlot();
    }
  };

  return (
    <div className="hidden lg:flex lg:flex-col lg:gap-6 lg:w-full">
      {/* First Slot Preference Block */}
      <SlotPreferenceBlock
        slotNumber={1}
        language={language}
        selectedSlot={firstSlot}
        isExpanded={stateMachine.isFirstSlotExpanded}
        onEdit={stateMachine.onEditFirstSlot}
        onDone={stateMachine.onDoneEditingFirstSlot}
      >
        <div className="flex justify-center items-start gap-8">
          {/* Calendar Column */}
          <div className="flex-shrink-0">
            <CalendarComponent
              currentDate={firstSlotDate}
              onDateSelect={handleFirstDateSelect}
              fromDate={fromDate}
              maxDate={maxDate}
              language={language}
              selectedTimeSlots={selectedTimeSlots}
              isDateDisabled={isDateDisabledForFirstSlot}
              preference="first"
              hasTimeSlotForDate={hasTimeSlotForDate}
              fastBookingSlots={fastBookingSlots}
              isLoading={isLoading}
              externalSlots={externalSlots}
              showSlotIndicators={showSlotIndicators}
            />
          </div>

          {/* Time Slots Column */}
          <div className="flex-grow">
            {firstSlotDate &&
              onTimeSelect &&
              setCurrentStep &&
              setBookingType && (
                <TimeSlotSelector
                  currentDate={firstSlotDate}
                  bookingType={bookingType}
                  setBookingType={setBookingType}
                  onTimeSelect={handleFirstTimeSelect}
                  language={language}
                  selectedTimeSlots={selectedTimeSlots}
                  onDeleteTimeSlot={onDeleteTimeSlot!}
                  setCurrentStep={setCurrentStep}
                  selectedClinic={selectedClinic}
                  onBack={onBack}
                  preference="first"
                  isEditing={stateMachine.isFirstSlotExpanded}
                  showSlotIndicators={showSlotIndicators}
                  useExternalSlotsOnly={useExternalSlotsOnly}
                />
              )}
          </div>
        </div>
      </SlotPreferenceBlock>

      {/* Second Slot Preference Block - Visible when first slot is not being edited */}
      {shouldRenderSecondSlot && (
        <div className="animate-in fade-in slide-in-from-top-4 duration-300">
          <SlotPreferenceBlock
            slotNumber={2}
            language={language}
            selectedSlot={secondSlot}
            isExpanded={stateMachine.isSecondSlotExpanded}
            onEdit={
              canEditSecondSlot
                ? secondSlot
                  ? stateMachine.onEditSecondSlot
                  : stateMachine.onAddSecondSlot
                : undefined
            }
            onDone={stateMachine.onDoneEditingSecondSlot}
            onDelete={handleDeleteSecondSlot}
            hasFirstSlot={!!firstSlot}
          >
            <div className="flex justify-center items-start gap-8">
              {/* Calendar Column */}
              <div className="flex-shrink-0">
                <CalendarComponent
                  currentDate={secondSlotDate}
                  onDateSelect={handleSecondDateSelect}
                  fromDate={fromDate}
                  maxDate={maxDate}
                  language={language}
                  selectedTimeSlots={selectedTimeSlots}
                  isDateDisabled={isDateDisabledForSecondSlot}
                  preference="second"
                  hasTimeSlotForDate={hasTimeSlotForDate}
                  fastBookingSlots={fastBookingSlots}
                  isLoading={isLoading}
                  externalSlots={externalSlots}
                  showSlotIndicators={showSlotIndicators}
                />
              </div>

              {/* Time Slots Column */}
              <div className="flex-grow">
                {secondSlotDate &&
                  onTimeSelect &&
                  setCurrentStep &&
                  setBookingType && (
                    <TimeSlotSelector
                      currentDate={secondSlotDate}
                      bookingType={bookingType}
                      setBookingType={setBookingType}
                      onTimeSelect={handleSecondTimeSelect}
                      language={language}
                      selectedTimeSlots={selectedTimeSlots}
                      onDeleteTimeSlot={onDeleteTimeSlot!}
                      setCurrentStep={setCurrentStep}
                      selectedClinic={selectedClinic}
                      onBack={onBack}
                      preference="second"
                      isEditing={stateMachine.isSecondSlotExpanded}
                      showSlotIndicators={showSlotIndicators}
                      useExternalSlotsOnly={useExternalSlotsOnly}
                    />
                  )}
              </div>
            </div>
          </SlotPreferenceBlock>
        </div>
      )}
    </div>
  );
};

export default DesktopDateStep;
